import { useState } from "react";
import { Crown, ArrowRight, X } from "lucide-react";
import { useSubscription, TRIAL_DAYS } from "@/hooks/use-subscription";
import { useSubscribeModal } from "./SubscribeModalProvider";

const DISMISS_KEY = "trialBannerDismissedAt";

export default function TrialExpiringBanner() {
  const { isTrialActive, trialDaysLeft, loading } = useSubscription();
  const { openSubscribe } = useSubscribeModal();
  // Fermeture mémorisée par jour restant : la bannière réapparaît une fois par
  // jour (quand trialDaysLeft change) au lieu de revenir à chaque rechargement.
  const [dismissedAt, setDismissedAt] = useState<string | null>(() =>
    localStorage.getItem(DISMISS_KEY)
  );

  if (loading || !isTrialActive || trialDaysLeft === null) return null;
  if (dismissedAt === String(trialDaysLeft)) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(trialDaysLeft));
    setDismissedAt(String(trialDaysLeft));
  };

  const lastDay = trialDaysLeft === 1;
  // Proportion de l'essai déjà écoulée (barre de progression en bas).
  const elapsed = Math.min(100, Math.max(6, ((TRIAL_DAYS - trialDaysLeft) / TRIAL_DAYS) * 100));

  return (
    <div className="relative overflow-hidden border-b border-primary/15">
      {/* dégradé d'ambiance, dominante à gauche */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(90deg, hsl(var(--primary)/0.14), hsl(var(--primary)/0.04) 38%, hsl(var(--card)) 80%)",
        }}
      />

      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 px-11 py-2.5">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-primary/15 text-primary ring-1 ring-inset ring-primary/25">
          <Crown className="h-3.5 w-3.5" />
        </span>

        <p className="text-sm text-foreground/85">
          {lastDay ? (
            <>
              <span
                className="font-bold text-highlight"
                style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}
              >
                Dernier jour
              </span>{" "}
              pour profiter du plan&nbsp;Pro.
            </>
          ) : (
            <>
              Encore{" "}
              <span
                className="font-semibold text-primary tabular-nums"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {trialDaysLeft} jours
              </span>{" "}
              pour profiter du plan&nbsp;Pro.
            </>
          )}
        </p>

        <button
          onClick={() => openSubscribe()}
          className="group inline-flex shrink-0 items-center gap-1.5 rounded-full bg-highlight px-4 py-1.5 text-xs font-semibold text-highlight-foreground shadow-sm shadow-highlight/30 transition-all duration-200 hover:-translate-y-px hover:shadow-md hover:shadow-highlight/40"
        >
          Je m'abonne
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
        </button>
      </div>

      <button
        onClick={dismiss}
        aria-label="Fermer"
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/50 transition-colors hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>

      {/* progression de l'essai (se remplit à mesure que les jours passent) */}
      <div
        aria-hidden
        className="absolute bottom-0 left-0 h-px bg-primary/60 transition-all"
        style={{ width: `${elapsed}%` }}
      />
    </div>
  );
}
